import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { userDetails } from "../Redux/ActionCreators/authAction";
import { loadCompanyMembers } from "../Redux/ActionCreators/chatAction";
import {
  fetchJobAssignmentSettings,
  fetchJobAccessList,
  updateCandidateAssignee,
} from "../Redux/ActionCreators/jobsAction";

// ── Candidate-row field readers ──────────────────────────────────────────────
// The list API returns the assignee as `assigned_company_user_id` (+ name/email);
// the `assigned_to` fallbacks cover a nested object shape.
export const getAssigneeId = (c) =>
  c?.assigned_company_user_id ||
  c?.assigned_to?.company_user_id ||
  c?.assigned_to?.id ||
  (typeof c?.assigned_to === "string" ? c.assigned_to : null) ||
  null;

const getAssigneeUserId = (c) => c?.assigned_user_id ?? c?.assigned_to?.user_id ?? null;

const roleText = (role) => role?.code || role?.name || role?.role_name || String(role || "");
const isAdminRole = (role) => /admin|owner/i.test(roleText(role));
const isRecruiterRole = (role) => /recruiter/i.test(roleText(role));

// Works out what the logged-in user may do with each candidate on this job.
//
// Rows are restricted only when the job has team access AND round-robin ON and the
// user is neither an admin nor a job-team member with full candidate access —
// the same rule the backend uses for candidate visibility.
const useCandidateAccess = (jobId) => {
  const dispatch = useDispatch();
  const me = useSelector((state) => state.auth.meDetails);

  const [companyMembers, setCompanyMembers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [acl, setAcl] = useState([]);
  const [accessLoading, setAccessLoading] = useState(true);

  useEffect(() => {
    if (!me) dispatch(userDetails());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      setAccessLoading(true);
      const [membersRes, settingsRes, aclRes] = await Promise.all([
        dispatch(loadCompanyMembers()),
        dispatch(fetchJobAssignmentSettings(jobId)),
        dispatch(fetchJobAccessList(jobId)),
      ]);
      if (cancelled) return;
      setCompanyMembers((membersRes?.data || []).filter((m) => m.id));
      setSettings(settingsRes.success ? settingsRes.data || {} : null);
      setAcl(aclRes.success && Array.isArray(aclRes.data) ? aclRes.data.filter((a) => a.is_active !== false) : []);
      setAccessLoading(false);
    })();
    return () => { cancelled = true; };
  }, [jobId, dispatch]);

  // auth/me gives user_id (int); candidates carry the company_user UUID. Bridge the two
  // via auth/me's company_user id (if present) and the company-members list.
  const myCompanyUserIds = useMemo(() => {
    const ids = new Set();
    (me?.companies || []).forEach((co) => {
      const cu = co?.company_user || {};
      if (cu.id) ids.add(String(cu.id));
      if (cu.company_user_id) ids.add(String(cu.company_user_id));
    });
    if (me?.user_id != null) {
      companyMembers
        .filter((m) => String(m.user_id) === String(me.user_id))
        .forEach((m) => ids.add(String(m.id)));
    }
    return ids;
  }, [me, companyMembers]);

  const isAdmin = useMemo(
    () => (me?.companies || []).some((co) => (co?.company_user?.roles || []).some(isAdminRole)),
    [me]
  );

  const isRecruiter = useMemo(
    () => (me?.companies || []).some((co) => (co?.company_user?.roles || []).some(isRecruiterRole)),
    [me]
  );

  const hasFullCandidateAccess = useMemo(
    () => acl.some((a) => a.full_candidate_access && myCompanyUserIds.has(String(a.company_user_id))),
    [acl, myCompanyUserIds]
  );

  // Admin or full candidate access → can see every candidate and (re)assign.
  const canManageAssignments = isAdmin || hasFullCandidateAccess;
  const teamAccessEnabled = !!settings?.team_access_enabled;
  const roundRobinEnabled = !!settings?.round_robin_enabled;
  // Recruiters without full candidate access only work on their own candidates whenever
  // team access is on (unassigned ones included), even if round-robin is off.
  const isRestricted = !accessLoading && teamAccessEnabled && !canManageAssignments && (roundRobinEnabled || isRecruiter);

  const isAssignedToMe = useCallback((candidate) => {
    const assigneeId = getAssigneeId(candidate);
    if (assigneeId && myCompanyUserIds.has(String(assigneeId))) return true;
    const assigneeUserId = getAssigneeUserId(candidate);
    return assigneeUserId != null && me?.user_id != null && String(assigneeUserId) === String(me.user_id);
  }, [myCompanyUserIds, me]);

  const canAccessCandidate = useCallback(
    (candidate) => !isRestricted || isAssignedToMe(candidate),
    [isRestricted, isAssignedToMe]
  );

  const memberById = useMemo(() => Object.fromEntries(companyMembers.map((m) => [String(m.id), m])), [companyMembers]);

  const getAssignee = useCallback((candidate) => {
    const id = getAssigneeId(candidate);
    if (!id) return null;
    const m = memberById[String(id)];
    const name = candidate?.assigned_user_name || candidate?.assigned_to?.name || m?.name || m?.email || `${String(id).slice(0, 8)}…`;
    return {
      id,
      name,
      email: candidate?.assigned_user_email || candidate?.assigned_to?.email || m?.email || "",
      source: candidate?.assignment_source || null,
      isMe: isAssignedToMe(candidate),
    };
  }, [memberById, isAssignedToMe]);

  // People a candidate can be assigned to: the job team if there is one, else all company members.
  const assignableMembers = useMemo(() => {
    if (acl.length) {
      return acl.map((a) => {
        const m = memberById[String(a.company_user_id)];
        return { id: a.company_user_id, name: m?.name || m?.email || `${String(a.company_user_id).slice(0, 8)}…` };
      });
    }
    return companyMembers.map((m) => ({ id: m.id, name: m.name || m.email }));
  }, [acl, companyMembers, memberById]);

  const setAssignee = useCallback(
    (candidateId, companyUserId) => dispatch(updateCandidateAssignee(jobId, candidateId, companyUserId)),
    [dispatch, jobId]
  );

  return {
    accessLoading,
    teamAccessEnabled,
    roundRobinEnabled,
    isRestricted,
    canManageAssignments,
    canAccessCandidate,
    getAssignee,
    assignableMembers,
    setAssignee,
  };
};

export default useCandidateAccess;
